// eslint-disable-next-line no-restricted-imports
import { HelpHelpCenterAri, HelpPageAri } from '@atlassian/ari/help';

const PORTAL_PAGE_PREFIX = 'portal_page-';

const getSafeHelpCenterIdMeta = (
	helpCenterAri: string,
): { activationId: string; helpCenterId: string } | undefined => {
	try {
		if (helpCenterAri && helpCenterAri !== 'null' && HelpHelpCenterAri.check(helpCenterAri)) {
			const { activationId, helpCenterId } = HelpHelpCenterAri.parse(helpCenterAri);
			return { activationId, helpCenterId };
		}
	} catch {
		// Silently fail for invalid ARIs
	}
};

/**
 * The type of page being viewed or edited in a hub.
 */
export enum HubPageType {
	/** The hub's home/landing page */
	HOME = 'HOME',
	/** A custom page created in the hub editor */
	CUSTOM = 'CUSTOM',
	/** A portal page backed by a JSM portal */
	PORTAL = 'PORTAL',
}

/**
 * Determines the type of hub page based on the pageId.
 *
 * - Portal pages have IDs prefixed with "portal_page-" (e.g. "portal_page-68").
 * - Custom pages have a non-empty pageId that doesn't start with the portal prefix.
 * - The home page has an empty pageId.
 */
export const getHubPageType = (pageId: string): HubPageType => {
	if (!pageId) {
		return HubPageType.HOME;
	}
	if (pageId.startsWith(PORTAL_PAGE_PREFIX)) {
		return HubPageType.PORTAL;
	}
	return HubPageType.CUSTOM;
};

/**
 * Check if the given pageId represents a portal page.
 */
export const isPortalPage = (pageId: string): boolean =>
	getHubPageType(pageId) === HubPageType.PORTAL;

/**
 * Extract the portal ID from a portal page ID.
 *
 * Portal page IDs have the format "portal_page-{portalId}" (e.g. "portal_page-68").
 * Returns the portalId portion (e.g. "68"), or an empty string if the pageId
 * is not a portal page.
 */
export const getPortalIdFromPageId = (pageId: string): string => {
	if (!isPortalPage(pageId)) {
		return '';
	}
	return pageId.slice(PORTAL_PAGE_PREFIX.length);
};

/**
 * Build the full page ARI for a portal page.
 *
 * Portal page ARIs include the helpCenterId as a third segment:
 *   ari:cloud:help::page/{activationId}/{portalPageId}/{helpCenterId}
 *
 * @param helpCenterAri - The full help center ARI to extract activationId and helpCenterId from.
 * @param portalPageId  - The portal page ID (e.g. "portal_page-68").
 * @returns The portal page ARI, or an empty string if the help center ARI is invalid.
 */
export const buildPortalPageAri = (helpCenterAri: string, portalPageId: string): string => {
	const meta = getSafeHelpCenterIdMeta(helpCenterAri);
	if (!meta) {
		return '';
	}
	const { activationId, helpCenterId } = meta;
	return `ari:cloud:help::page/${activationId}/${portalPageId}/${helpCenterId}`;
};

/**
 * Check if a parsed page ID corresponds to the home page by comparing
 * it with the help center ID extracted from the help center ARI.
 *
 * When navigating via page ARIs, the home page's parsed pageId equals
 * the helpCenterId (e.g. "helpcenter-456"), not an empty string.
 */
export const isHomePageByAri = (parsedPageId: string, helpCenterAri: string): boolean => {
	const meta = getSafeHelpCenterIdMeta(helpCenterAri);
	if (!meta) {
		return false;
	}
	return parsedPageId === meta.helpCenterId;
};

type PageEntry = {
	id: string;
	name?: { default?: string | null } | null | undefined;
};

type ResolvedPage = {
	/** Whether this is the home page. */
	isHomePage: boolean;
	/** The full page ARI for use in mutations. Empty string for home pages. */
	pageAri: string;
	/** The page display name. Empty string for home and portal pages. */
	pageName: string;
	/** The type of page being viewed. */
	pageType: HubPageType;
};

/**
 * Resolve the current page context from the given pageId.
 *
 * For portal pages, constructs the correct ARI with helpCenterId appended.
 * For custom pages, looks up the page in filteredPages to get its ARI and name.
 * For the home page (empty pageId), returns isHomePage = true.
 *
 * @param currentPageName - Optional name for the current page, typically sourced from the
 *   currentPage query. Used as a fallback when the page is not found in filteredPages
 *   (e.g. portal pages).
 */
export const resolveCurrentPage = (
	pageId: string,
	filteredPages: ReadonlyArray<PageEntry>,
	helpCenterAri: string,
	currentPageName?: string,
): ResolvedPage => {
	const pageType = getHubPageType(pageId);

	if (pageType === HubPageType.PORTAL) {
		return {
			pageType,
			isHomePage: false,
			pageAri: buildPortalPageAri(helpCenterAri, pageId),
			pageName: currentPageName || '',
		};
	}

	if (pageType === HubPageType.CUSTOM) {
		const matchedPage = filteredPages.find(
			(page) => page.name?.default && HelpPageAri.parse(page.id).pageId === pageId,
		);

		if (matchedPage) {
			return {
				pageType,
				isHomePage: false,
				pageAri: matchedPage.id,
				pageName: matchedPage.name?.default || '',
			};
		}
	}

	return {
		pageType: HubPageType.HOME,
		isHomePage: true,
		pageAri: '',
		pageName: '',
	};
};
